import { useCallback, useMemo } from 'react';
import PortalIdParser from 'PortalIdParser';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import useUserInfo from 'framework-listing-lib/internal/hooks/useUserInfo';
import { useViewProps as useViewPropsContext } from 'framework-listing-lib/internal/providers/ListingProvider';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { useCurrentViewContext } from 'framework-listing-lib/internal/ViewTabs/providers/CurrentViewProvider';
import { useViews as useViewsProvider } from 'framework-listing-lib/internal/ViewTabs/providers/ViewsProvider';
import { getViewId } from 'framework-listing-lib/internal/ViewTabs/utils/views';
const DEFAULT_SAVE_AS_NEW_PROPS = {
  disabled: false
};

/**
 * façade over the context hook in case implementation changes
 */
function useViewProps() {
  const viewProps = useViewPropsContext();
  return viewProps;
}
export function useHasViews() {
  const viewProps = useViewProps();
  return useMemo(() => Boolean(viewProps && viewProps.hasViews), [viewProps]);
}
export function useOnViewIdChange() {
  const viewProps = useViewProps();
  return useCallback((viewId, options) => {
    if (viewProps && viewProps.onViewIdChange) {
      viewProps.onViewIdChange(viewId, options);
    }
    listingLibObserver.setBulkActionSelectionMode('none');
  }, [viewProps]);
}
export function useViewsNamespace() {
  const viewProps = useViewProps();
  return useMemo(() => viewProps && viewProps.namespace ? viewProps.namespace : 'NONE', [viewProps]);
}
export function useGetSaveAsNewViewProps() {
  const viewProps = useViewProps();
  const currentView = useCurrentView();
  return useMemo(() => {
    if (!currentView || !viewProps || !viewProps.getSaveAsNewViewProps) {
      return DEFAULT_SAVE_AS_NEW_PROPS;
    }
    return viewProps.getSaveAsNewViewProps(currentView);
  }, [currentView, viewProps]);
}
export function useRenderTab() {
  const viewProps = useViewProps();
  return useMemo(() => viewProps && viewProps.renderTab ? viewProps.renderTab : undefined, [viewProps]);
}
export function useFiltersForNewViews() {
  const viewProps = useViewProps();
  return useMemo(() => Object.assign({
    filterGroups: [],
    quickFilters: []
  }, viewProps && viewProps.filtersForNewViews ? viewProps.filtersForNewViews || {} : {}), [viewProps]);
}
export function useViewPropsCurrentViewId() {
  const viewProps = useViewProps();
  return useMemo(() => viewProps && viewProps.currentViewId ? viewProps.currentViewId : undefined, [viewProps]);
}
export function useCurrentViewId() {
  const isFoldersMode = useIsFoldersMode();
  const {
    currentView
  } = useCurrentViewContext();
  if (isFoldersMode) {
    return undefined;
  }
  return currentView ? getViewId(currentView) : undefined;
}
export function useCurrentView() {
  const isFoldersMode = useIsFoldersMode();
  const {
    currentView
  } = useCurrentViewContext();
  if (isFoldersMode) {
    return undefined;
  }
  return currentView;
}
export function useIsCurrentViewReadOnly() {
  const currentView = useCurrentView();
  const {
    userInfo
  } = useUserInfo();
  return Boolean(userInfo && currentView && userInfo.user.user_id !== currentView.ownerId);
}
export function useCurrentViewFilters() {
  const currentView = useCurrentView();
  return useMemo(() => {
    if (currentView) {
      return {
        filterGroups: currentView.filterGroups,
        quickFilters: currentView.quickFilters || []
      };
    }
    return {
      filterGroups: [],
      quickFilters: []
    };
  }, [currentView]);
}
export function useViewUpdateState() {
  const {
    viewUpdateState,
    setViewUpdateState
  } = useViewsProvider();
  return [viewUpdateState, setViewUpdateState];
}
export function useViewColumns() {
  const {
    columns
  } = useViewsProvider();
  return columns;
}
export function useSetViewColumns() {
  const {
    setColumns
  } = useViewsProvider();
  return setColumns;
}
export function useNumberOfFrozenColumns() {
  const {
    numberOfFrozenColumns
  } = useViewsProvider();
  return useMemo(() => numberOfFrozenColumns, [numberOfFrozenColumns]);
}
export function useSetNumberOfFrozenColumns() {
  const {
    setNumberOfFrozenColumns
  } = useViewsProvider();
  return setNumberOfFrozenColumns;
}
export function useSetCardProperties() {
  const {
    setCardProperties
  } = useViewsProvider();
  return setCardProperties;
}
export function useCardProperties() {
  const {
    cardProperties
  } = useViewsProvider();
  return cardProperties;
}
export function useQuickFilterPropertyNamesState() {
  const {
    quickFilterPropertyNames
  } = useViewsProvider();
  return quickFilterPropertyNames;
}
export function useGetAllViewsUrl() {
  const viewProps = useViewProps();
  const objectType = useObjectTypeId();
  const {
    userInfo
  } = useUserInfo();
  const namespace = useViewsNamespace();
  const urlSearchParams = useMemo(() => {
    const draftParms = new URLSearchParams();
    draftParms.append('namespace', namespace);
    draftParms.append('objectTypeId', objectType);
    draftParms.append('returnLink', `${window.location.pathname}${window.location.search}`);
    if (userInfo !== null && userInfo !== void 0 && userInfo.user) {
      draftParms.append('ownerId', `${userInfo.user.user_id}`);
    }
    return draftParms;
  }, [namespace, objectType, userInfo]);
  return useMemo(() => viewProps && viewProps.allViewsUrl ? `${viewProps.allViewsUrl}?${urlSearchParams.toString()}` : `/manage-views/${PortalIdParser.get()}/all?${urlSearchParams.toString()}`, [urlSearchParams, viewProps]);
}